import { createSlice } from "@reduxjs/toolkit";
import api from "../../Services/ApiServices";
import utils from "../../Services/Utils";
import {
  IAccountOpeningDocument,
  IAccountOpeningDocumentCancelReason,
} from "../../Types/AOD";
import AuthInfoContext from "../../Types/AuthInfoContext";
import {
  GetAccountOpeningDocumentSearchOptions,
  UploadAndRegisterAccountOpeningDocumentInsertObject,
} from "../../Types/InsertObjects";
import { dispatch, RootState } from "../store";
import { setStage } from "./common";

interface IDocumentUploadState {
  isLoading: boolean;
  error: any;
  lstUploadedDocument?: IAccountOpeningDocument[];
  lstDocumentCancelReasonList?: IAccountOpeningDocumentCancelReason[];
  lstLivePhotoIPVCode?: any;
  base64?: string;
  refreshLoading?: boolean;
  refreshError?: any;
  isPhotoRejected?: boolean;

  uploadLoading: boolean;
  uploadError: any;
  uploadSuccess?: any;

  deleteLoading: boolean;
  deleteError: any;
  deleteSuccess?: any;

  loadLoading: boolean;
  loadError: any;
  loadSuccess?: any;

  latitude?: number;
  longitude?: number;
  locationInfo?: string;

  polingPercent: number;
  showPercent: boolean;

  continueQueryMessage?: string;
}

const initialState: IDocumentUploadState = {
  isLoading: false,
  error: null,
  uploadLoading: false,
  uploadError: null,
  deleteLoading: false,
  deleteError: null,
  loadLoading: false,
  loadError: null,
  polingPercent: 0,
  showPercent: false,
  isPhotoRejected: false,
};

const slice = createSlice({
  name: "stage5",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = undefined;
    },
    changeFieldsForReCapture(state) {
      state.base64 = undefined;
      state.lstUploadedDocument = [];
      state.loadError = undefined;
      state.uploadError = undefined;
      state.continueQueryMessage = undefined;
    },
    documentGetListSuccess(state, action) {
      if (action.payload.Records)
        state.lstUploadedDocument = action.payload.Records;
      if (action.payload.Records1)
        state.lstDocumentCancelReasonList = action.payload.Records1;
      if (action.payload.Records2)
        state.lstLivePhotoIPVCode = action.payload.Records2;
      state.isLoading = false;
      state.refreshLoading = false;
    },
    uploadSuccess(state, action) {
      state.uploadLoading = false;
      state.uploadSuccess = action.payload;
    },
    uploadError(state, action) {
      state.uploadLoading = false;
      state.uploadError = action.payload;
      state.isPhotoRejected = true;
    },
    startUploadLoading(state) {
      state.uploadLoading = true;
      state.uploadError = undefined;
      state.continueQueryMessage = undefined;
    },

    deleteSuccess(state, action) {
      state.deleteLoading = false;
      state.deleteSuccess = action.payload;
      state.base64 = "";
    },
    deleteError(state, action) {
      state.deleteLoading = false;
      state.deleteError = action.payload;
    },
    startDeleteLoading(state) {
      state.deleteLoading = true;
      state.deleteError = undefined;
    },

    loadSuccess(state, action) {
      state.loadLoading = false;
      state.loadSuccess = action.payload;
      state.base64 = utils.convertToDataURIFromBase64(
        action.payload.Base64DataOrErrorMessage
      );
    },
    loadError(state, action) {
      state.loadLoading = false;
      state.loadError = action.payload;
    },
    startLoadLoading(state) {
      state.loadLoading = true;
      state.loadError = undefined;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    refreshStartLoading(state) {
      state.refreshLoading = true;
      state.refreshError = undefined;
    },
    refreshHasError(state, action) {
      state.refreshLoading = false;
      state.refreshError = action.payload;
    },
    setBase64(state, action) {
      state.base64 = action.payload;
      state.deleteLoading = false;
      state.isPhotoRejected = false;
    },
    setLocation(state, action) {
      state.latitude = action.payload.latitude;
      state.longitude = action.payload.longitude;
      state.locationInfo = JSON.stringify(action.payload);
    },
    setPolingPercent(state, action) {
      state.polingPercent = action.payload;
    },
    setShowPoling(state, action) {
      state.showPercent = action.payload;
    },
    setContinueQueryMessage(state, action) {
      state.continueQueryMessage = action.payload;
    },
    resetFields() {
      return { ...initialState };
    },
  },
});

// Reducer
export default slice.reducer;
export const {
  setBase64,
  loadSuccess,
  loadError,
  startLoadLoading,
  resetFields,
  setLocation,
  setContinueQueryMessage,
} = slice.actions;

export const getAccountOpeningDocumentList = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.startLoading());

      const { objAuthInfoResponse } = getState().common;

      let response = await api.GetAccountOpeningDocumentList({
        AOSAI: JSON.stringify(objAuthInfoResponse),
        ILPS: 1,
      });

      await dispatch(slice.actions.documentGetListSuccess(response));
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.hasError(ex));
    }
  };
};

export const getAccountOpeningDocument = (
  objUploadedDocument: IAccountOpeningDocument
) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(startLoadLoading());
      if (!objUploadedDocument) return;
      const { objAuthInfoResponse } = getState().common;
      let io = new GetAccountOpeningDocumentSearchOptions();

      io.AOSAI = JSON.stringify(objAuthInfoResponse);

      io.AODID = objUploadedDocument.AccountOpeningDocumentID;
      io.AODUID = objUploadedDocument.AccountOpeningDocumentUID;
      // let response = res(io)
      let response = await api.GetAccountOpeningDocumentDetail(io);
      if (!response.Status) throw new Error(response.Base64DataOrErrorMessage);
      await dispatch(loadSuccess(response));
      // loadSuccess(response);
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(loadError(ex));
    }
  };
};

export const uploadDocument = (
  base64: string = "",
  AccountOpeningDocumentTypeInternalCode: number
) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.startUploadLoading());
      const io = new UploadAndRegisterAccountOpeningDocumentInsertObject();
      const { objAuthInfoResponse } = getState().common;
      const stage5 = getState().stage5;
      io.AOSAI = JSON.stringify(objAuthInfoResponse);
      io.AODTIC = AccountOpeningDocumentTypeInternalCode;
      io.AODOFN = "ipv.png";
      io.AODB64D = utils.convertToBase64FromDataURI(base64);
      // prompt("io.AODB64D", io.AODB64D);
      if (stage5.latitude && stage5.longitude) {
        io.LGLog = stage5.latitude;
        io.LGLat = stage5.longitude;
        io.LGLI = stage5.locationInfo;
      }

      let response = await api.UploadAndRegisterAccountOpeningDocument(io);

      if (!response.Status) throw new Error(response.Message);

      // await dispatch(slice.actions.setShowPoling(true));
      // const checkStatus = await checkDocumentUploadStatus(
      //   response.AccountOpeningDocumentID,
      //   response.AccountOpeningDocumentUID,
      //   (percent) => {
      //     dispatch(slice.actions.setPolingPercent(percent));
      //   }
      // );
      // await dispatch(slice.actions.setShowPoling(false));

      // if (
      //   checkStatus?.AccountOpeningDocumentParseStatusInternalCode ===
      //   Enums.ACCOUNT_OPENING_DOCUMENT_PARSE_STATUS.VERIFICATION_FAILED
      // ) {
      //   throw new Error(checkStatus.Message);
      // }

      // if (
      //   checkStatus?.AccountOpeningDocumentParseStatusInternalCode ===
      //   Enums.ACCOUNT_OPENING_DOCUMENT_PARSE_STATUS.WARNING
      // ) {
      //   tryEnqueueSnackbar(checkStatus.Message, { variant: "warning" });
      // }

      await dispatch(slice.actions.uploadSuccess(response));

      dispatch(setStage(8.0));
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.setShowPoling(false));
      await dispatch(slice.actions.uploadError(ex));
    }
  };
};

export const deleteDocument = (AODID: number, AODUID: string) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.startDeleteLoading());
      // lstUploadedDocument.
      const { objAuthInfoResponse } = getState().common;
      let response = await api.DeleteUploadedDocument({
        AOSAI: JSON.stringify(objAuthInfoResponse),
        AODID: AODID,
        AODUID: AODUID,
      });

      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );
      if (!objAuthContext.Status) throw new Error(objAuthContext.Message);

      await dispatch(slice.actions.deleteSuccess(response));
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.deleteError(ex));
    }
  };
};

export const fetchLocation = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      window.navigator.geolocation.getCurrentPosition(
        (result) => {
          dispatch(slice.actions.setLocation(result.coords));
        },
        (error) => {
          console.log("Error occurred while fetching location", error);
        }
      );
    } catch (ex) {}
  };
};

export const initiateRecapture = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      dispatch(slice.actions.changeFieldsForReCapture());
    } catch (ex) {}
  };
};
